import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { supabase } from '../lib/supabase'

interface ProtectedRouteProps {
  children: ReactNode
  requireAdmin?: boolean
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { isAuthenticated, isAdmin, isApproved, isPending, loading, profile } = useAuthStore()

  // Debug logging with storage health check
  console.log('ProtectedRoute - loading:', loading, 'isAuthenticated:', isAuthenticated(), 'profile:', profile)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="mb-4">Lädt...</p>
          <p className="text-sm text-gray-500 mb-4">
            Authentifizierung wird überprüft...
          </p>
          <button 
            onClick={() => {
              console.log('🧹 User requested auth state clear - forcing storage cleanup')
              const authStore = useAuthStore.getState()
              
              // Clear all auth storage
              localStorage.clear()
              sessionStorage.clear()
              
              // Clear auth state
              authStore.clearAuthState()
              
              // Also clear any Supabase session
              supabase.auth.signOut()
              
              // Force navigate to login after cleanup
              setTimeout(() => {
                window.location.href = '/login'
              }, 100)
            }}
            className="btn btn-danger"
          >
            Storage Leeren & Neu Anmelden
          </button>
        </div>
      </div>
    )
  }

  if (!isAuthenticated()) {
    // Clear any stale data before redirecting to prevent conflicts
    console.log('🧹 Not authenticated - clearing storage before redirect')
    localStorage.clear()
    return <Navigate to="/login" replace />
  }

  // Check if user is pending approval
  if (isPending()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">⏳</span>
          </div>
          <h2 className="text-xl font-bold text-yellow-800 mb-2">Account wartet auf Genehmigung</h2>
          <p className="text-yellow-700 mb-4">
            Ihr Account wurde erfolgreich erstellt, muss aber noch von einem Administrator genehmigt werden.
          </p>
          <p className="text-sm text-yellow-600 mb-4">
            Kontaktieren Sie einen Administrator, um Ihren Account freizuschalten.
          </p>
          <button 
            onClick={() => {
              useAuthStore.getState().signOut()
            }}
            className="btn btn-warning"
          >
            Abmelden
          </button>
        </div>
      </div>
    )
  }

  // Check if user is approved
  if (!isApproved() && profile?.status !== 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-6 bg-red-50 border border-red-200 rounded-lg">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">❌</span>
          </div>
          <h2 className="text-xl font-bold text-red-800 mb-2">Account nicht genehmigt</h2>
          <p className="text-red-700 mb-4">
            Ihr Account wurde nicht genehmigt oder wurde abgelehnt.
          </p>
          <p className="text-sm text-red-600 mb-4">
            Kontaktieren Sie einen Administrator für weitere Informationen.
          </p>
          <button 
            onClick={() => {
              useAuthStore.getState().signOut()
            }}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Abmelden
          </button>
        </div>
      </div>
    )
  }

  if (requireAdmin && !isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Zugriff verweigert</h1>
          <p className="text-gray-600 mb-6">
            Sie haben keine Berechtigung, auf diese Seite zuzugreifen. 
            Nur Administratoren können auf die Admin-Funktionen zugreifen.
          </p>
          <button 
            onClick={() => window.history.back()}
            className="btn btn-primary"
          >
            Zurück
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
